import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  ArrowLeft,
  Upload,
  FileText,
  Check,
  Loader2,
  Sparkles,
  TrendingUp,
  AlertCircle,
  Target,
  Download,
} from "lucide-react";
import "./AuditPage.css";

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═════════════════════════════════════════════════════════════ */

const API = process.env.REACT_APP_BACKEND_URL;
const WA_NUMBER = "917996325666";

const COUNTRIES = ["USA", "Canada", "Other"];
const EXPERIENCE = ["0-2 years", "3-5 years", "6-10 years", "10+ years"];
const DURATIONS = ["< 1 month", "1-3 months", "3-6 months", "6+ months"];
const APPS_PER_WEEK = ["< 10", "10-25", "25-50", "50+"];
const HOURS_PER_DAY = ["< 1", "1-2", "2-4", "4+"];
const CHANNELS = [
  "LinkedIn Easy Apply",
  "Indeed",
  "Company career pages",
  "Referrals / networking",
  "Recruiters",
];
const TAILORING = ["Always", "Sometimes", "Never"];

/* Steps in the flow (used for progress bar) */
const STEPS = [
  "hook",           // 0
  "role",           // 1
  "country",        // 2
  "experience",     // 3
  "duration",       // 4
  "apps_per_week",  // 5
  "hours_per_day",  // 6
  "channels",       // 7
  "tailor",         // 8
  "interviews",     // 9
  "frustration",    // 10
  "resume",         // 11
  "lead",           // 12
  "loading",        // 13
  "diagnosis",      // 14
  "fork",           // 15
];

const TOTAL_QUESTION_STEPS = 12; // hook + 10 questions + resume + lead

/* ═══════════════════════════════════════════════════════════════
   Small building blocks
   ═════════════════════════════════════════════════════════════ */

const OptionList = ({ options, value, onChange, testidPrefix }) => (
  <div className="aud-option-grid">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        className={`aud-option ${value === opt ? "aud-option-selected" : ""}`}
        onClick={() => onChange(opt)}
        data-testid={`${testidPrefix}-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
      >
        {opt}
        <span className="aud-option-check" aria-hidden>
          {value === opt && <Check className="w-3.5 h-3.5" />}
        </span>
      </button>
    ))}
  </div>
);

const MultiOptionList = ({ options, values, onToggle, testidPrefix }) => (
  <div className="aud-option-grid">
    {options.map((opt) => {
      const selected = values.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          className={`aud-option ${selected ? "aud-option-selected" : ""}`}
          onClick={() => onToggle(opt)}
          data-testid={`${testidPrefix}-${opt.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
        >
          {opt}
          <span className="aud-option-check" aria-hidden>
            {selected && <Check className="w-3.5 h-3.5" />}
          </span>
        </button>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   Main page
   ═════════════════════════════════════════════════════════════ */

export default function AuditPage() {
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState("");

  // Answers
  const [targetRole, setTargetRole] = useState("");
  const [country, setCountry] = useState("");
  const [experience, setExperience] = useState("");
  const [duration, setDuration] = useState("");
  const [appsPerWeek, setAppsPerWeek] = useState("");
  const [hoursPerDay, setHoursPerDay] = useState("");
  const [channels, setChannels] = useState([]);
  const [tailor, setTailor] = useState("");
  const [interviewsSoFar, setInterviewsSoFar] = useState("");
  const [frustration, setFrustration] = useState("");

  // Resume
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeText, setResumeText] = useState("");
  const [resumeUploading, setResumeUploading] = useState(false);

  // Lead
  const [firstName, setFirstName] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");

  // Diagnosis
  const [diagnosis, setDiagnosis] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [loadingStep, setLoadingStep] = useState(0);

  const step = STEPS[stepIdx];

  useEffect(() => {
    document.title = "Why am I not getting interviews — Free Audit | BulkJobApply";
    window.scrollTo({ top: 0, behavior: "smooth" });
    setError("");
  }, [stepIdx]);

  /* Progress calc — hook = 0%, lead capture = ~92%, results = 100% */
  const progressPct = useMemo(() => {
    if (step === "hook") return 4;
    if (step === "diagnosis" || step === "fork") return 100;
    if (step === "loading") return 96;
    const questionIndex = STEPS.indexOf(step); // 1..12
    return Math.round((questionIndex / TOTAL_QUESTION_STEPS) * 88) + 4;
  }, [step]);

  const goNext = useCallback(() => {
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  }, []);
  const goBack = useCallback(() => {
    setStepIdx((i) => Math.max(i - 1, 0));
  }, []);

  /* ── Resume upload ── */
  const handleResumeSelect = async (file) => {
    if (!file) return;
    setError("");
    setResumeFile(file);
    setResumeUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/audit/upload-resume`, {
        method: "POST",
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      setResumeText(d.text || "");
    } catch (e) {
      setError(e.message);
      setResumeFile(null);
    } finally {
      setResumeUploading(false);
    }
  };

  /* ── Analyze + Save lead ── */
  const submitLeadAndAnalyze = async () => {
    setError("");
    if (!firstName.trim() || !whatsapp.trim()) {
      setError("Please share your first name and WhatsApp number.");
      return;
    }
    setStepIdx(STEPS.indexOf("loading"));
    // Kick loading step animation
    setLoadingStep(0);
    const loadingTimers = [
      setTimeout(() => setLoadingStep(1), 900),
      setTimeout(() => setLoadingStep(2), 2400),
      setTimeout(() => setLoadingStep(3), 4200),
    ];

    const answersPayload = {
      target_role: targetRole,
      country,
      experience,
      duration,
      apps_per_week: appsPerWeek,
      hours_per_day: hoursPerDay,
      apply_channels: channels,
      tailor_resume: tailor,
      interviews_so_far: parseInt(interviewsSoFar, 10) || 0,
      frustration: frustration.slice(0, 1000),
    };

    try {
      // 1) LLM
      const r = await fetch(`${API}/api/audit/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: answersPayload, resume_text: resumeText }),
      });
      const diag = await r.json();
      if (!r.ok) throw new Error(diag.detail || "Analysis failed");
      setDiagnosis(diag);

      // 2) Store lead (fire-and-remember)
      const leadRes = await fetch(`${API}/api/audit/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          whatsapp: `${countryCode}${whatsapp.replace(/\D/g, "")}`,
          email: email.trim() || null,
          answers: answersPayload,
          resume_text: resumeText,
          diagnosis: diag,
        }),
      });
      const leadData = await leadRes.json();
      if (leadRes.ok) setLeadId(leadData.id);

      setStepIdx(STEPS.indexOf("diagnosis"));
    } catch (e) {
      setError(e.message);
      setStepIdx(STEPS.indexOf("lead"));
    } finally {
      loadingTimers.forEach(clearTimeout);
    }
  };

  /* ── Fork path recording + WhatsApp deep link ── */
  const recordPath = (path) => {
    if (!leadId) return;
    fetch(`${API}/api/audit/leads/${leadId}/path`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
    }).catch(() => {});
  };

  const handleWhatsapp = () => {
    recordPath("bja");
    // Fire Google Ads conversion (defined in public/index.html)
    if (typeof window.gtag_report_conversion === "function") {
      window.gtag_report_conversion();
    }
    const msg = `Hi! I just did the interview audit — I want BulkJobApply to handle my applications. My target role is ${targetRole}.`;
    const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDiy = () => {
    recordPath("diy");
    // Generate a text summary file
    const lines = [
      `Your BulkJobApply Interview Audit — DIY Checklist`,
      `Prepared for ${firstName || "you"} · ${new Date().toLocaleDateString()}`,
      ``,
      `TARGET ROLE: ${targetRole}`,
      `MARKET: ${country}   EXPERIENCE: ${experience}`,
      ``,
      `WHAT YOU'RE DOING NOW`,
      diagnosis?.current_summary || "",
      ``,
      `THE REAL PROBLEM`,
      diagnosis?.core_problem || "",
      ``,
      `THE MATH (desired output vs required input)`,
      diagnosis?.output_vs_input || "",
      ``,
      `RESUME FIXES`,
      ...(diagnosis?.resume_fixes || []).map((f, i) => `${i + 1}. ${f}`),
      ``,
      `STRATEGY FIXES`,
      ...(diagnosis?.strategy_fixes || []).map((f, i) => `${i + 1}. ${f}`),
      ``,
      `— BulkJobApply · bulkjobapply.com`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `interview_audit_${(firstName || "you").replace(/\s+/g, "_").toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* ═══════════════════════════════════════════════════════════
     Step renderers
     ═════════════════════════════════════════════════════════ */

  const renderStep = () => {
    switch (step) {
      case "hook":
        return (
          <section className="aud-screen aud-hero" data-testid="aud-hook">
            <span className="aud-hero-eyebrow">Free · 2 minutes · No signup</span>
            <h1 className="aud-hero-title">
              Find out <em>why</em> you&apos;re not getting interview calls.
            </h1>
            <p className="aud-hero-sub">
              A free 2-minute audit of your job search. Real diagnosis, real fixes — no fluff.
            </p>
            <button
              type="button"
              className="aud-cta-primary"
              onClick={goNext}
              data-testid="aud-start"
            >
              Start my free audit
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="aud-trust">
              <span className="aud-trust-dot" />
              Used by job seekers applying in the US market.
            </p>
          </section>
        );

      case "role":
        return (
          <Question label="Step 1 of 10" title="What role are you targeting?" onNext={goNext} onBack={goBack} nextDisabled={!targetRole.trim()}>
            <input
              className="aud-input"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              placeholder="e.g., Senior Backend Engineer, Product Manager"
              autoFocus
              data-testid="aud-input-role"
            />
          </Question>
        );

      case "country":
        return (
          <Question label="Step 2 of 10" title="Where are you applying?" onNext={goNext} onBack={goBack} nextDisabled={!country}>
            <OptionList options={COUNTRIES} value={country} onChange={(v) => { setCountry(v); }} testidPrefix="aud-opt-country" />
          </Question>
        );

      case "experience":
        return (
          <Question label="Step 3 of 10" title="Years of experience?" onNext={goNext} onBack={goBack} nextDisabled={!experience}>
            <OptionList options={EXPERIENCE} value={experience} onChange={setExperience} testidPrefix="aud-opt-exp" />
          </Question>
        );

      case "duration":
        return (
          <Question label="Step 4 of 10" title="How long have you been job searching?" onNext={goNext} onBack={goBack} nextDisabled={!duration}>
            <OptionList options={DURATIONS} value={duration} onChange={setDuration} testidPrefix="aud-opt-dur" />
          </Question>
        );

      case "apps_per_week":
        return (
          <Question label="Step 5 of 10" title="Roughly how many jobs do you apply to per week?" onNext={goNext} onBack={goBack} nextDisabled={!appsPerWeek}>
            <OptionList options={APPS_PER_WEEK} value={appsPerWeek} onChange={setAppsPerWeek} testidPrefix="aud-opt-apps" />
          </Question>
        );

      case "hours_per_day":
        return (
          <Question label="Step 6 of 10" title="How many hours per day do you spend on your job search?" onNext={goNext} onBack={goBack} nextDisabled={!hoursPerDay}>
            <OptionList options={HOURS_PER_DAY} value={hoursPerDay} onChange={setHoursPerDay} testidPrefix="aud-opt-hrs" />
          </Question>
        );

      case "channels":
        return (
          <Question
            label="Step 7 of 10"
            title="Where do you mostly apply?"
            help="Tap all that apply."
            onNext={goNext}
            onBack={goBack}
            nextDisabled={channels.length === 0}
          >
            <MultiOptionList
              options={CHANNELS}
              values={channels}
              onToggle={(opt) =>
                setChannels((c) => (c.includes(opt) ? c.filter((x) => x !== opt) : [...c, opt]))
              }
              testidPrefix="aud-opt-chan"
            />
          </Question>
        );

      case "tailor":
        return (
          <Question label="Step 8 of 10" title="Do you tailor your resume for each job?" onNext={goNext} onBack={goBack} nextDisabled={!tailor}>
            <OptionList options={TAILORING} value={tailor} onChange={setTailor} testidPrefix="aud-opt-tailor" />
          </Question>
        );

      case "interviews":
        return (
          <Question
            label="Step 9 of 10"
            title="How many interview calls have you gotten so far?"
            onNext={goNext}
            onBack={goBack}
            nextDisabled={interviewsSoFar === "" || isNaN(parseInt(interviewsSoFar, 10))}
          >
            <input
              className="aud-input"
              type="number"
              inputMode="numeric"
              min="0"
              value={interviewsSoFar}
              onChange={(e) => setInterviewsSoFar(e.target.value)}
              placeholder="e.g., 2"
              autoFocus
              data-testid="aud-input-interviews"
            />
          </Question>
        );

      case "frustration":
        return (
          <Question
            label="Step 10 of 10"
            title="What frustrates you most right now?"
            help="One sentence is fine. This helps the audit get sharper."
            onNext={goNext}
            onBack={goBack}
            nextDisabled={false}
          >
            <textarea
              className="aud-input aud-textarea"
              value={frustration}
              onChange={(e) => setFrustration(e.target.value)}
              placeholder="e.g., I'm applying nonstop but hearing nothing back."
              maxLength={1000}
              data-testid="aud-input-frustration"
            />
          </Question>
        );

      case "resume":
        return (
          <Question
            label="Almost done"
            title="Upload your resume so I can audit it too."
            help="PDF or DOCX. Your audit gets much sharper with this — we only use it to generate your diagnosis, not to store anywhere else."
            onNext={goNext}
            onBack={goBack}
            nextDisabled={resumeUploading}
            nextLabel="Continue"
          >
            <label
              className={`aud-drop ${resumeUploading ? "aud-drop-active" : ""}`}
              htmlFor="aud-resume-file"
              data-testid="aud-drop-zone"
            >
              <div className="aud-drop-icon">
                {resumeUploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
              </div>
              <div className="aud-drop-title">
                {resumeUploading ? "Reading your resume…" : "Tap to upload PDF or DOCX"}
              </div>
              <div className="aud-drop-sub">Max 5 MB · Never shared with third parties</div>
              <input
                id="aud-resume-file"
                type="file"
                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="aud-drop-hidden"
                onChange={(e) => handleResumeSelect(e.target.files?.[0] || null)}
                data-testid="aud-resume-input"
              />
            </label>
            {resumeFile && !resumeUploading && (
              <div className="aud-file-chip" data-testid="aud-resume-attached">
                <span>
                  <FileText className="w-4 h-4" style={{ display: "inline", marginRight: 6, verticalAlign: "-3px" }} />
                  {resumeFile.name} · {resumeText.length.toLocaleString()} chars extracted
                </span>
                <button
                  className="aud-file-remove"
                  onClick={() => { setResumeFile(null); setResumeText(""); }}
                  aria-label="Remove resume"
                  type="button"
                >×</button>
              </div>
            )}
            <button className="aud-skip" type="button" onClick={goNext} data-testid="aud-resume-skip">
              Skip for now — I&apos;ll do it with just my answers
            </button>
          </Question>
        );

      case "lead":
        return (
          <section className="aud-screen" data-testid="aud-lead">
            <div className="aud-q-label">Your audit is ready</div>
            <h2 className="aud-q-title">Where should I send it?</h2>
            <p className="aud-q-help">
              We&apos;ll show your diagnosis right after this. WhatsApp is used only if you ask
              BulkJobApply to take over — never spam.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <input
                className="aud-input"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                data-testid="aud-input-first-name"
              />
              <div className="aud-input-tel">
                <input
                  className="aud-input"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  placeholder="+1"
                  data-testid="aud-input-cc"
                  aria-label="Country code"
                />
                <input
                  className="aud-input"
                  inputMode="numeric"
                  placeholder="WhatsApp number"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  autoComplete="tel"
                  data-testid="aud-input-whatsapp"
                />
              </div>
              <input
                className="aud-input"
                type="email"
                placeholder="Email (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="aud-input-email"
              />
            </div>
            {error && <div className="aud-error" data-testid="aud-error">{error}</div>}
            <div className="aud-nav">
              <button className="aud-back" type="button" onClick={goBack} data-testid="aud-back">
                <ArrowLeft className="w-4 h-4" style={{ display: "inline", marginRight: 4 }} />
                Back
              </button>
              <button
                className="aud-next"
                type="button"
                onClick={submitLeadAndAnalyze}
                data-testid="aud-show-results"
              >
                Show my results
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        );

      case "loading":
        return (
          <section className="aud-screen aud-loading" data-testid="aud-loading">
            <div className="aud-loading-spinner" />
            <div className="aud-loading-title">Analyzing your search…</div>
            <div className="aud-loading-sub">This is honest, not a template. Takes about 15 seconds.</div>
            <div className="aud-loading-steps">
              <div className={`aud-loading-step ${loadingStep >= 0 ? (loadingStep > 0 ? "aud-loading-step-done" : "aud-loading-step-active") : ""}`}>
                {loadingStep > 0 ? <Check className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                Reading your form answers
              </div>
              <div className={`aud-loading-step ${loadingStep >= 1 ? (loadingStep > 1 ? "aud-loading-step-done" : "aud-loading-step-active") : ""}`}>
                {loadingStep > 1 ? <Check className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                Cross-checking your resume vs {targetRole || "your target role"}
              </div>
              <div className={`aud-loading-step ${loadingStep >= 2 ? (loadingStep > 2 ? "aud-loading-step-done" : "aud-loading-step-active") : ""}`}>
                {loadingStep > 2 ? <Check className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
                Running the volume math
              </div>
              <div className={`aud-loading-step ${loadingStep >= 3 ? "aud-loading-step-active" : ""}`}>
                <Loader2 className="w-4 h-4 animate-spin" />
                Writing your diagnosis
              </div>
            </div>
          </section>
        );

      case "diagnosis":
        return (
          <section className="aud-screen aud-diag" data-testid="aud-diagnosis">
            <span className="aud-diag-eyebrow">Your audit</span>
            <h2 className="aud-diag-title">
              Here&apos;s what&apos;s actually going on, {firstName || "friend"}.
            </h2>

            <div className="aud-diag-block">
              <div className="aud-diag-block-lbl">
                <Target className="w-3.5 h-3.5" />
                What you&apos;re doing now
              </div>
              <div className="aud-diag-block-body">{diagnosis?.current_summary}</div>
            </div>

            <div className="aud-diag-block aud-diag-block-red">
              <div className="aud-diag-block-lbl">
                <AlertCircle className="w-3.5 h-3.5" />
                The real problem
              </div>
              <div className="aud-diag-block-body">{diagnosis?.core_problem}</div>
            </div>

            <div className="aud-diag-block aud-diag-block-math">
              <div className="aud-diag-block-lbl">
                <TrendingUp className="w-3.5 h-3.5" />
                Desired output vs required input
              </div>
              <div className="aud-diag-block-body">{diagnosis?.output_vs_input}</div>
            </div>

            <div className="aud-diag-block">
              <div className="aud-diag-block-lbl">
                <Sparkles className="w-3.5 h-3.5" />
                Resume fixes
              </div>
              <ul className="aud-diag-list">
                {(diagnosis?.resume_fixes || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>

            <div className="aud-diag-block">
              <div className="aud-diag-block-lbl">
                <Sparkles className="w-3.5 h-3.5" />
                Strategy fixes
              </div>
              <ul className="aud-diag-list">
                {(diagnosis?.strategy_fixes || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>

            <button
              className="aud-cta-primary"
              onClick={() => setStepIdx(STEPS.indexOf("fork"))}
              data-testid="aud-see-paths"
              style={{ marginTop: 8 }}
            >
              See your two paths
              <ArrowRight className="w-5 h-5" />
            </button>
          </section>
        );

      case "fork":
        return (
          <section className="aud-screen aud-fork" data-testid="aud-fork">
            <h2 className="aud-fork-title">Two ways forward.</h2>
            <p className="aud-fork-sub">Both fix the problem. One takes your time — one doesn&apos;t.</p>
            <div className="aud-fork-cards">
              <div className="aud-fork-card aud-fork-card-diy">
                <span className="aud-fork-card-tag">OPTION A · DIY</span>
                <h3 className="aud-fork-card-title">Do it yourself</h3>
                <p className="aud-fork-card-body">
                  You can absolutely do this alone. We&apos;ll give you the full audit and checklist.
                </p>
                <div className="aud-fork-card-cost">
                  Realistic cost: 40–60 hrs/month tailoring + submitting, plus emotional fatigue.
                  Most people burn out in week 2–3.
                </div>
                <button
                  className="aud-fork-card-btn"
                  onClick={handleDiy}
                  data-testid="aud-diy"
                  type="button"
                >
                  <Download className="w-4 h-4" />
                  Get my DIY checklist
                </button>
              </div>

              <div className="aud-fork-card aud-fork-card-bja">
                <span className="aud-fork-card-tag">OPTION B · DONE-FOR-YOU</span>
                <h3 className="aud-fork-card-title">Hand it to BulkJobApply</h3>
                <p className="aud-fork-card-body">
                  Our team applies to 780+ jobs a month FOR you — tailored resumes, submitted by hand
                  on real company career pages, not Easy Apply. You just show up and interview.
                </p>
                <div className="aud-fork-card-cost">
                  93% of clients land within 3 months. You keep 100% of your salary — no commission.
                </div>
                <button
                  className="aud-fork-card-btn"
                  onClick={handleWhatsapp}
                  data-testid="aud-bja"
                  type="button"
                >
                  Talk to BulkJobApply on WhatsApp
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>
        );

      default:
        return null;
    }
  };

  return (
    <div className="audit-shell" data-testid="audit-page">
      <header className="aud-header">
        <div className="aud-header-inner">
          <a href="/" className="aud-brand" data-testid="aud-brand">
            <span className="aud-brand-dot" />
            BulkJobApply
          </a>
          {step !== "hook" && step !== "fork" && (
            <>
              <div className="aud-progress-track" aria-hidden>
                <div className="aud-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
              <span className="aud-progress-lbl">{progressPct}%</span>
            </>
          )}
        </div>
      </header>
      <main className="aud-main">{renderStep()}</main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Question layout (used for every single-question step)
   ═════════════════════════════════════════════════════════════ */

function Question({ label, title, help, children, onNext, onBack, nextDisabled, nextLabel = "Continue" }) {
  return (
    <section className="aud-screen">
      <div className="aud-q-label">{label}</div>
      <h2 className="aud-q-title">{title}</h2>
      {help && <p className="aud-q-help">{help}</p>}
      {children}
      <div className="aud-nav">
        <button className="aud-back" type="button" onClick={onBack} data-testid="aud-back">
          <ArrowLeft className="w-4 h-4" style={{ display: "inline", marginRight: 4 }} />
          Back
        </button>
        <button
          className="aud-next"
          type="button"
          onClick={onNext}
          disabled={nextDisabled}
          data-testid="aud-next"
        >
          {nextLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
